import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IError } from '@axmit/error-helper';
import { message } from 'antd';
import { AppDispatch } from 'app/store';
import {
  ERegionSuccessMessage,
  IGroupCollection,
  IGroupCollectionFilter,
  IGroupCreateData,
  IGroupUpdateData,
  IRegionCollection,
  IRegionCollectionFilter,
  IRegionCreateData,
  IRegionUpdateData,
} from 'entities/Region/Region.models';
import { regionTransport } from 'entities/Region/Region.transport';

export interface IState {
  regionCollectionLoading: boolean;
  regionCollection: IRegionCollection | null;
  regionCollectionError: IError | null;
  groupCollectionLoading: boolean;
  groupCollection: IGroupCollection | null;
  groupCollectionError: IError | null;
}

const initialState: IState = {
  regionCollectionLoading: false,
  regionCollection: null,
  regionCollectionError: null,
  groupCollectionLoading: false,
  groupCollection: null,
  groupCollectionError: null,
};

export const regionSlice = createSlice({
  name: 'region',
  initialState,
  reducers: {
    setRegionCollectionLoading(state, action: PayloadAction<boolean>) {
      state.regionCollectionLoading = action.payload;
    },
    setRegionCollection(state, action: PayloadAction<IRegionCollection | null>) {
      state.regionCollection = action.payload;
    },
    setRegionCollectionError(state, action: PayloadAction<IError | null>) {
      state.regionCollectionError = action.payload;
    },
    setGroupCollectionLoading(state, action: PayloadAction<boolean>) {
      state.groupCollectionLoading = action.payload;
    },
    setGroupCollection(state, action: PayloadAction<IGroupCollection | null>) {
      state.groupCollection = action.payload;
    },
    setGroupCollectionError(state, action: PayloadAction<IError | null>) {
      state.groupCollectionError = action.payload;
    },
  },
});

export const {
  setRegionCollectionLoading,
  setRegionCollection,
  setRegionCollectionError,
  setGroupCollectionLoading,
  setGroupCollection,
  setGroupCollectionError,
} = regionSlice.actions;
export default regionSlice.reducer;

// regions

export const getRegionCollection = (filter?: IRegionCollectionFilter) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setRegionCollectionLoading(true));

    try {
      // ponytail: management page renders a flat un-paginated tree, so fetch the full set
      // (BE default limit is 10, max 100) — otherwise the 11th+ region is silently dropped.
      const regionCollection = await regionTransport.getRegionCollection({ limit: 100, ...filter });

      dispatch(setRegionCollection(regionCollection));
    } catch (error) {
      dispatch(setRegionCollectionError(error as IError));
    } finally {
      dispatch(setRegionCollectionLoading(false));
    }
  }

  return thunk;
};

export const addRegion = (params: IRegionCreateData, onSuccess?: () => void) => {
  async function thunk(dispatch: AppDispatch) {
    try {
      await regionTransport.addRegion(params);

      message.success(ERegionSuccessMessage.RegionCreate);
      onSuccess?.();
      dispatch(getRegionCollection());
    } catch (error) {
      dispatch(setRegionCollectionError(error as IError));
    }
  }

  return thunk;
};

export const updateRegion = (params: IRegionUpdateData, onSuccess?: () => void) => {
  async function thunk(dispatch: AppDispatch) {
    try {
      await regionTransport.updateRegion(params);

      message.success(ERegionSuccessMessage.RegionEdit);
      onSuccess?.();
      dispatch(getRegionCollection());
    } catch (error) {
      dispatch(setRegionCollectionError(error as IError));
    }
  }

  return thunk;
};

export const deleteRegion = (id: string) => {
  async function thunk(dispatch: AppDispatch) {
    try {
      await regionTransport.deleteRegion(id);

      message.success(ERegionSuccessMessage.RegionDelete);
      dispatch(getRegionCollection());
    } catch (error) {
      dispatch(setRegionCollectionError(error as IError));
    }
  }

  return thunk;
};

// groups

export const getGroupCollection = (filter?: IGroupCollectionFilter) => {
  async function thunk(dispatch: AppDispatch) {
    dispatch(setGroupCollectionLoading(true));

    try {
      // ponytail: same as regions — fetch the full set so groups beyond the 10th aren't dropped.
      const groupCollection = await regionTransport.getGroupCollection({ limit: 100, ...filter });

      dispatch(setGroupCollection(groupCollection));
    } catch (error) {
      dispatch(setGroupCollectionError(error as IError));
    } finally {
      dispatch(setGroupCollectionLoading(false));
    }
  }

  return thunk;
};

export const addGroup = (params: IGroupCreateData, onSuccess?: () => void) => {
  async function thunk(dispatch: AppDispatch) {
    try {
      await regionTransport.addGroup(params);

      message.success(ERegionSuccessMessage.GroupCreate);
      onSuccess?.();
      dispatch(getGroupCollection());
    } catch (error) {
      dispatch(setGroupCollectionError(error as IError));
    }
  }

  return thunk;
};

export const updateGroup = (params: IGroupUpdateData, onSuccess?: () => void) => {
  async function thunk(dispatch: AppDispatch) {
    try {
      await regionTransport.updateGroup(params);

      message.success(ERegionSuccessMessage.GroupEdit);
      onSuccess?.();
      dispatch(getGroupCollection());
    } catch (error) {
      dispatch(setGroupCollectionError(error as IError));
    }
  }

  return thunk;
};

export const deleteGroup = (id: string) => {
  async function thunk(dispatch: AppDispatch) {
    try {
      await regionTransport.deleteGroup(id);

      message.success(ERegionSuccessMessage.GroupDelete);
      dispatch(getGroupCollection());
    } catch (error) {
      dispatch(setGroupCollectionError(error as IError));
    }
  }

  return thunk;
};
